import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { PlusIcon, MagnifyingGlassIcon, BookOpenIcon, BuildingOfficeIcon, SparklesIcon, RocketLaunchIcon, TrophyIcon, PlayIcon } from '@heroicons/react/24/outline';
import { useClassrooms } from '../hooks/useClassrooms';
import { useAuth } from '../contexts/AuthContext';
import ClassroomCard from '../components/ClassroomCard';
import LoadingSpinner from '../components/LoadingSpinner';
import AIChatButton from '../components/AIChatButton';
import Pagination from '../components/Pagination';


const PAGE_SIZE = 6;

const Classrooms: React.FC = React.memo(() => {
  const { currentUser } = useAuth();
  const { classrooms, loading, error, getPublicClassrooms, fetchEnrolledCourseClassrooms, getAuthorClassrooms, getAllClassrooms } = useClassrooms();

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedTerm, setDebouncedTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const initializedRef = useRef(false);

  // Determine user role and permissions
  const normalizedRole = (currentUser?.role || '').toString().trim().toLowerCase();
  const admin = normalizedRole === 'admin';
  const author = normalizedRole === 'author' && currentUser?.status !== 'pending';
  const canCreateClassrooms = admin || author;
  // One-time load: fetch classrooms based on user role
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    
    if (admin) {
      getAllClassrooms();
    } else if (author) {
      getAuthorClassrooms();
    } else if (currentUser) {
      fetchEnrolledCourseClassrooms();
    } else {
      getPublicClassrooms();
    }
  }, [currentUser, admin, author, fetchEnrolledCourseClassrooms, getPublicClassrooms, getAuthorClassrooms, getAllClassrooms]);

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setDebouncedTerm(searchTerm.trim().toLowerCase()), 350);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Build categories
  const categories = useMemo(() => {
    const set = new Set<string>();
    classrooms.forEach(c => { if (c.category) set.add(String(c.category)); });
    return ['all', ...Array.from(set).sort()];
  }, [classrooms]);

  // Filtered list
  const filtered = useMemo(() => {
    const term = debouncedTerm;
    return classrooms.filter((c) => {
      const categoryMatch = selectedCategory === 'all' || String(c.category) === selectedCategory;
      if (!categoryMatch) return false;
      if (!term) return true;
      const name = String(c.name || '').toLowerCase();
      const desc = String(c.description || '').toLowerCase();
      const cat = String(c.category || '').toLowerCase();
      return name.includes(term) || desc.includes(term) || cat.includes(term);
    });
  }, [classrooms, selectedCategory, debouncedTerm]);

  // Pagination calculations
  const totalItems = filtered.length;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE);
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const endIndex = startIndex + PAGE_SIZE;
  const visible = useMemo(() => filtered.slice(startIndex, endIndex), [filtered, startIndex, endIndex]);


  const resetFilters = useCallback(() => {
    setSelectedCategory('all');
    setSearchTerm('');
    setCurrentPage(1);
  }, []);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, debouncedTerm]);

  // Handle page change
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
    // Scroll to top when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Get classroom stats
  const stats = useMemo(() => {
    const totalClassrooms = classrooms.length;
    const activeClassrooms = classrooms.filter(c => c.status === 'active').length;
    const inactiveClassrooms = totalClassrooms - activeClassrooms;
    
    return {
      totalClassrooms,
      activeClassrooms,
      inactiveClassrooms,
    };
  }, [classrooms]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 overflow-x-hidden">
      {/* Hero Section */}
      <section className="hero-section relative bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 overflow-hidden">
        {/* Main Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-12" style={{ width: '100%', maxWidth: '100vw' }}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-8 items-center" style={{ width: '100%' }}>
            
            {/* Left Side - Text Content */}
            <div className="text-center lg:text-left space-y-3 sm:space-y-4" style={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
              {/* Welcome Badge */}
              <div className="inline-flex items-center px-3 py-1.5 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full text-white text-xs font-medium">
                <BuildingOfficeIcon className="h-3 w-3 mr-1.5 text-blue-300" />
                Collaborative Learning Spaces
              </div>

              {/* Main Headline */}
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-white">
                <span className="block">Join Interactive</span>
                <span className="block bg-gradient-to-r from-blue-200 via-cyan-200 to-indigo-200 bg-clip-text text-transparent">
                  Classrooms
                </span>
              </h1>

              {/* Subtitle */}
              <p className="text-base sm:text-lg text-blue-100 leading-relaxed max-w-2xl mx-auto lg:mx-0">
                Connect with peers, participate in discussions, and enhance your learning experience through collaborative classroom communities.
              </p>

              {/* Quick Stats */}
              <div className="grid grid-cols-3 gap-2 py-3" style={{ width: '100%', maxWidth: '100%' }}>
                <div className="text-center">
                  <div className="text-lg sm:text-xl font-bold text-white mb-0.5">
                    {stats.totalClassrooms}
                  </div>
                  <div className="text-xs text-blue-200">Total Classrooms</div>
                </div>
                <div className="text-center">
                  <div className="text-lg sm:text-xl font-bold text-white mb-0.5">
                    {stats.activeClassrooms}
                  </div>
                  <div className="text-xs text-blue-200">Active</div>
                </div>
                <div className="text-center">
                  <div className="text-lg sm:text-xl font-bold text-white mb-0.5">
                    {stats.inactiveClassrooms}
                  </div>
                  <div className="text-xs text-blue-200">Inactive</div>
                </div>
              </div>

              {/* Call-to-Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 justify-center lg:justify-start" style={{ width: '100%', maxWidth: '100%' }}>
                <button
                  onClick={() => {
                    const searchInput = document.querySelector('input[placeholder*="Search"]') as HTMLInputElement;
                    searchInput?.focus();
                  }}
                  className="inline-flex items-center justify-center px-3 sm:px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-semibold rounded-lg hover:from-blue-600 hover:to-indigo-700 transition-all duration-200 shadow-lg text-sm"
                >
                  <RocketLaunchIcon className="h-4 w-4 mr-1.5" />
                  Search Classrooms
                </button>
                {currentUser && canCreateClassrooms && (
                  <Link
                    to="/app/admin"
                    className="inline-flex items-center justify-center px-3 sm:px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white font-semibold rounded-lg hover:bg-white/20 transition-all duration-200 text-sm"
                  >
                    <PlusIcon className="h-4 w-4 mr-1.5" />
                    Create Classroom
                  </Link>
                )}
              </div>
            </div>

            {/* Right Side - Visual Elements - Hidden on mobile for stability */}
            <div className="relative hidden xl:block">
              {/* Classroom Activity Cards */}
              <div className="space-y-4">
                {/* Active Classroom Card */}
                <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-cyan-500 rounded-full flex items-center justify-center">
                      <BuildingOfficeIcon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold">Active Learning</h3>
                      <p className="text-blue-200 text-sm">{stats.totalClassrooms} classrooms available</p>
                    </div>
                  </div>
                </div>

                {/* Community Card */}
                <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 ml-8">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-full flex items-center justify-center">
                      <TrophyIcon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold">Community</h3>
                      <p className="text-blue-200 text-sm">Connect with peers</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Wave Divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg className="w-full h-8 sm:h-12 text-gray-50" viewBox="0 0 1200 120" preserveAspectRatio="none">
            <path d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z" opacity=".25" fill="currentColor"></path>
            <path d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z" opacity=".5" fill="currentColor"></path>
            <path d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z" fill="currentColor"></path>
          </svg>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 relative z-20">
        {/* Controls */}
        <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4 mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-2 relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name, description, or category"
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full py-2 px-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                {categories.map((cat) => (
                  <option key={cat} value={cat}>{cat === 'all' ? 'All categories' : cat}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <LoadingSpinner />
              <p className="text-gray-600 mt-3">Loading classrooms...</p>
            </div>
          </div>
        ) : error ? (
          <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {author ? 'View or create your classrooms or manage' : 'Unable to load classrooms'}
            </h3>
            <p className="text-gray-600 mb-4">
              {author 
                ? 'Access the admin panel to view, create, or manage your classrooms.'
                : 'There was an issue loading classrooms. Please try again.'
              }
            </p>
            <div className="flex items-center justify-center gap-3">
              {currentUser && canCreateClassrooms && (
                <Link to="/app/admin" className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700">Create classroom</Link>
              )}
              {!author && (
                <button onClick={() => window.location.reload()} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200">Try again</button>
              )}
            </div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {author ? 'View or create your classrooms or manage' : 'No classrooms found'}
            </h3>
            <p className="text-gray-600 mb-4">
              {author 
                ? 'Access the admin panel to view, create, or manage your classrooms.'
                : 'Try adjusting your search or filters.'
              }
            </p>
            <div className="flex items-center justify-center gap-3">
              {!author && (
                <button onClick={resetFilters} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200">Clear filters</button>
              )}
              {currentUser && canCreateClassrooms && (
                <Link to="/app/admin" className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700">Create classroom</Link>
              )}
            </div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {visible.map((classroom) => (
                <ClassroomCard key={classroom.id} classroom={classroom} />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                itemsPerPage={PAGE_SIZE}
                totalItems={totalItems}
              />
            )}
          </>
        )}
      </div>


      {/* AI Chat Button */}
      <AIChatButton />
    </div>
  );
});

export default Classrooms;
